###
Utility methods for manipulating and normalizing file system paths.
###

separator := "/"

normalizePath := (path: string) =>
  path.replace(/\/+/g, separator) // /// -> /
  .replace(/\/[^/]*\/\.\.(\/|$)/g, separator) // /base/something/.. -> /base/
  .replace(/\/(\.(\/|$))*/g, separator) // /base/. -> /base/

normalizePath2 := (path: string) =>
  stack: string[] := []
  path.split(separator).forEach (part) =>
    switch part
      when ".", ""
        ;// Skip
      when ".."
        if stack.length
          stack.pop()
        else
          throw new Error `No upper directory when normalizing '${path}'`
      else
        stack.push part

  // Add leading and trailing slashes as necessary
  if path.startsWith '/'
    stack.unshift ''
  else if path.startsWith './' // Keep local paths
    stack.unshift '.'
  if path.match /\/\.{0,2}$/
    stack.push ''

  return stack.join(separator)

// By default throws an error when paths '../..' try go above the base path
// can be overridden by passing false as the third parameter.
absolutizePath := (base: string, relativePath: string, preventEscape=true) =>
  unless base.startsWith separator
    throw new Error "base path must be absolute"
  unless base.endsWith separator
    throw new Error "base path must be a directory"

  path := normalizePath `${base}${relativePath}`

  if preventEscape
    unless path.startsWith normalizePath base
      throw new Error "path escaped base directory (too many ../)"

  return path

allDirectories := /^.*\//

baseName := (path: string) =>
  path.replace(allDirectories, "")

extensionFor := (path: string) =>
    result := baseName(path).match /\.([^.]+)$/

    if result
      result[1]
    else
      ""

// Only media types for editible text types that have a special meaning to the
// browser. html, css, js, json all require the proper content type header to
// work properly so we make sure to have the correct data. Also setting the
// charset to utf-8 which is the only one that we currently support.
mimes :=
  css: "text/css"
  html: "text/html"
  js: "text/javascript"
  json: "application/json"
  md: "text/markdown"

textMediaType := (path: string) =>
  extension := extensionFor(path)
  type := mimes[extension as keyof typeof mimes] or "text/plain"

  `${type}; charset=utf-8`


baseDirectory := (absolutePath="/") =>
  absolutePath.match(allDirectories)?[0]

isRelativePath :=  (path: string) =>
  !!path.match /^.?.\//

fileSeparator := separator

Ergonomics := (fs: FileSystem) =>
  // Wrap read with more ergonomic text file writing
  write := fs.write

  fs.write = (path: string, stringOrBlob: string | Blob, ...args: any[]) =>
    let blob
    if typeof stringOrBlob is "string"
      blob = new Blob [stringOrBlob],
        type: textMediaType path
    else
      blob = stringOrBlob

    write.call(fs, path, blob, ...args)

  return fs

withoutAllExtensions := (path: string) =>
  path.replace(/\.[^\/]*$/,"")

withoutExtension := (path: string) =>
  path.replace(/\.[^\.\/]*$/,"")

export {
  Ergonomics
  absolutizePath
  baseDirectory
  baseName
  extensionFor
  fileSeparator
  isRelativePath
  normalizePath
  normalizePath2
  separator
  textMediaType
  withoutAllExtensions
  withoutExtension
}

{ type Bindable } from ../bindable.civet

export interface FileSystem extends InstanceType<ReturnType<typeof Bindable>>
  read(path: string, ...args: any[]): Promise<Blob & { path: string } | undefined>
  write(path: string, blob: Blob, ...args: any[]): Promise<void>
  delete(path: string, ...args: any[]): Promise<void>
  list(path: string, ...args: any[]): Promise<Entry[]>

export interface FileEntry
  path: string
  relativePath: string
  remotePath?: string
  size?: number

export interface FolderEntry
  folder: true
  path: string
  relativePath: string
  remotePath?: string

export type Entry = FileEntry | FolderEntry
